/**
 * ErrorBoundary — Catches React render errors.
 * Shows friendly UI instead of blank white screen.
 */
import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="error-boundary">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-500 mb-6">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B2A7B] text-white rounded-xl font-medium hover:bg-[#152163] transition-colors"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * ApiError — Reusable inline error UI for API failures.
 * Shows friendly message + optional retry button.
 */
export const ApiError = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-testid="api-error">
    <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-3">
      <AlertTriangle className="w-7 h-7 text-red-400" />
    </div>
    <p className="text-sm text-gray-600 mb-4">{message || 'Unable to load data'}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
        data-testid="api-retry-btn"
      >
        <RefreshCw className="w-4 h-4" />
        Retry
      </button>
    )}
  </div>
);
